import {
  Team,
  Player,
  MatchState,
  TeamState,
  LostReason,
  LOST_REASON_REQUIRES_WINNER,
  MIN_ROTATION_GAP,
  MATCH_CONFIG,
  MatchType,
  ValidationError,
  MatchRulesConfig,
  WIN_DIFFERENCE,
} from '@/types';
import { v4 as uuidv4 } from 'uuid';

// V-01: Match Number Required
export function validateMatchNumberRequired(matchNumber: number | null | undefined): ValidationError | null {
  if (matchNumber === null || matchNumber === undefined || matchNumber === 0) {
    return {
      id: uuidv4(),
      title: 'Match Number Required',
      message: 'Match number is required.',
    };
  }
  return null;
}

// V-02: Match Number Must Be Positive Integer
export function validateMatchNumberFormat(matchNumber: number): ValidationError | null {
  if (!Number.isInteger(matchNumber) || matchNumber <= 0) {
    return {
      id: uuidv4(),
      title: 'Invalid Match Number',
      message: 'Match number must be a positive number.',
    };
  }
  return null;
}

// V-03: Date Required
export function validateDateRequired(date: string | null | undefined): ValidationError | null {
  if (!date) {
    return {
      id: uuidv4(),
      title: 'Date Required',
      message: 'Match date is required.',
    };
  }
  return null;
}

// V-04 to V-05: Team Required
export function validateTeamRequired(team: Team | null, teamLabel: string): ValidationError | null {
  if (!team) {
    return {
      id: uuidv4(),
      title: 'Team Required',
      message: `Select ${teamLabel}.`,
    };
  }
  return null;
}

// V-06: Match Type Required
export function validateMatchTypeRequired(matchType: MatchType | null): ValidationError | null {
  if (!matchType) {
    return {
      id: uuidv4(),
      title: 'Match Type Required',
      message: 'Select a match type.',
    };
  }
  return null;
}

// V-07: Toss Winner Required
export function validateTossWinnerRequired(tossWonById: string | null): ValidationError | null {
  if (!tossWonById) {
    return {
      id: uuidv4(),
      title: 'Toss Winner Required',
      message: 'Select who won the toss.',
    };
  }
  return null;
}

// V-08: Toss Choice Required
export function validateTossChoiceRequired(tossChoice: 'serve' | 'side' | null): ValidationError | null {
  if (!tossChoice) {
    return {
      id: uuidv4(),
      title: 'Toss Choice Required',
      message: 'Select Serve or Side.',
    };
  }
  return null;
}

// V-09: Same Team Cannot Play Both Sides
export function validateTeamsDifferent(teamAId: string | null, teamBId: string | null): ValidationError | null {
  if (teamAId && teamBId && teamAId === teamBId) {
    return {
      id: uuidv4(),
      title: 'Duplicate Team Selection',
      message: 'Team B must be different from Team A.',
    };
  }
  return null;
}

// V-10: Toss Winner Must Be One of the Playing Teams
export function validateTossWinnerInMatch(
  tossWonById: string | null,
  teamAId: string | null,
  teamBId: string | null
): ValidationError | null {
  if (tossWonById && teamAId && teamBId) {
    if (tossWonById !== teamAId && tossWonById !== teamBId) {
      return {
        id: uuidv4(),
        title: 'Invalid Toss Winner',
        message: 'Toss winner must be one of the playing teams.',
      };
    }
  }
  return null;
}

// V-11: First Server Must Be From Serving Team
export function validateFirstServerTeam(
  player: Player | null,
  servingTeamId: string,
  servingTeamName: string
): ValidationError | null {
  if (!player) {
    return {
      id: uuidv4(),
      title: 'Invalid First Server',
      message: 'Please select a player.',
    };
  }

  if (player.teamId !== servingTeamId) {
    return {
      id: uuidv4(),
      title: 'INVALID FIRST SERVER',
      message: `Only ${servingTeamName} players can serve first. Please select a player from ${servingTeamName}.`,
    };
  }
  return null;
}

// V-12: Server Too Recently Served (Rotation Violation)
export function validateRotationGap(
  player: Player,
  teamState: TeamState,
  teamName: string
): ValidationError | null {
  const serveHistory = teamState.rotationState.serveHistory;

  // Find the player's most recent service stint
  const lastServedIndex = serveHistory.lastIndexOf(player.id);
  if (lastServedIndex === -1) {
    return null;
  }

  // Count how many different players served after the player's last stint
  const playersAfterLastStint = new Set(serveHistory.slice(lastServedIndex + 1));

  // We need at least MIN_ROTATION_GAP - 1 different players (MIN_ROTATION_GAP includes the player)
  // So MIN_ROTATION_GAP = 3 means we need 2 OTHER players
  const requiredOthers = MIN_ROTATION_GAP - 1;

  if (playersAfterLastStint.size < requiredOthers) {
    const remaining = requiredOthers - playersAfterLastStint.size;
    const plural = remaining === 1 ? 'player' : 'players';
    return {
      id: uuidv4(),
      title: 'ROTATION VIOLATION',
      message: `${player.name} has served too recently. At least ${requiredOthers} other ${teamName} players must serve before ${player.name} can serve again. ${remaining} more ${plural} needed.`,
    };
  }
  return null;
}

// V-13: Check if rotation deadlock has occurred
export function checkRotationDeadlock(teamState: TeamState): boolean {
  const eligiblePlayers = teamState.players.filter(p => {
    const lastServedIndex = teamState.rotationState.serveHistory.lastIndexOf(p.id);
    if (lastServedIndex === -1) return true;

    const playersAfterLastStint = new Set(teamState.rotationState.serveHistory.slice(lastServedIndex + 1));
    return playersAfterLastStint.size >= MIN_ROTATION_GAP - 1;
  });

  // If no players are eligible, we have a deadlock
  return eligiblePlayers.length === 0;
}

// V-14: No validation - continuous service is allowed
export function isContinuousService(servingTeamId: string, pointWonById: string): boolean {
  return servingTeamId === pointWonById;
}

// V-16, V-17: Point Winner Required for IN/TOUCH
export function validatePointWinnerRequired(lostReason: LostReason | null, pointWinnerId: string | null): ValidationError | null {
  if (lostReason && LOST_REASON_REQUIRES_WINNER[lostReason] && !pointWinnerId) {
    return {
      id: uuidv4(),
      title: 'PLAYER REQUIRED',
      message: `When the reason is ${lostReason}, the player who caught/touched the ball must be selected.`,
    };
  }
  return null;
}

// V-18 to V-21: Point Winner Forbidden for ACE/OUT/NET/FOUL
export function validatePointWinnerForbidden(lostReason: LostReason | null, pointWinnerId: string | null): ValidationError | null {
  if (lostReason && !LOST_REASON_REQUIRES_WINNER[lostReason] && pointWinnerId) {
    return {
      id: uuidv4(),
      title: 'Point Winner Not Allowed',
      message: `Point winner cannot be recorded for ${lostReason}.`,
    };
  }
  return null;
}

// V-22: Point Winner Must Be From the Rally-Winning Team
export function validatePointWinnerTeam(
  pointWinnerId: string | null,
  winningTeamId: string,
  winningTeamName: string,
  losingTeamName: string,
  teams: { id: string; players: Player[] }[]
): ValidationError | null {
  if (!pointWinnerId) return null;

  const pointWinnerPlayer = teams
    .flatMap(t => t.players)
    .find(p => p.id === pointWinnerId);

  if (pointWinnerPlayer && pointWinnerPlayer.teamId !== winningTeamId) {
    return {
      id: uuidv4(),
      title: 'WRONG TEAM',
      message: `The point winner must be a player from ${winningTeamName}, not ${losingTeamName}.`,
    };
  }
  return null;
}

// V-23: Normal Set Win Condition (now uses dynamic rules)
export function checkSetWin(
  scoreA: number,
  scoreB: number,
  rules?: MatchRulesConfig | null
): { winner: 'A' | 'B' | null; goldenPoint: boolean } {
  const winningPoint = rules?.winningPoint ?? 15;
  const goldenPoint = rules?.goldenPoint ?? 21;

  const maxScore = Math.max(scoreA, scoreB);
  const diff = Math.abs(scoreA - scoreB);

  // Check for golden point trigger
  const goldenPointTrigger = goldenPoint - 1;
  if (scoreA === goldenPointTrigger && scoreB === goldenPointTrigger) {
    return { winner: null, goldenPoint: true };
  }

  // V-26: Golden Point Win
  if (scoreA === goldenPoint) {
    return { winner: 'A', goldenPoint: false };
  }
  if (scoreB === goldenPoint) {
    return { winner: 'B', goldenPoint: false };
  }

  // Check normal win conditions
  if (maxScore >= winningPoint && diff >= WIN_DIFFERENCE) {
    return { winner: scoreA > scoreB ? 'A' : 'B', goldenPoint: false };
  }

  // V-24: Deuce - Continue Play
  return { winner: null, goldenPoint: false };
}

// V-25: Check if golden point mode (now uses dynamic rules)
export function isGoldenPointMode(scoreA: number, scoreB: number, rules?: MatchRulesConfig | null): boolean {
  const goldenPoint = rules?.goldenPoint ?? 21;
  const goldenPointTrigger = goldenPoint - 1;
  return scoreA === goldenPointTrigger && scoreB === goldenPointTrigger;
}

// V-28, V-29: Check if scoring is locked
export function isScoringLocked(matchState: MatchState): { locked: boolean; reason: string } {
  if (matchState.status === 'match-won') {
    return { locked: true, reason: 'Match is complete.' };
  }
  if (matchState.status === 'set-won' && matchState.currentSet) {
    return { locked: true, reason: 'Set is complete. Start next set to continue.' };
  }
  return { locked: false, reason: '' };
}

// Match Win Condition
export function checkMatchWin(setsWonA: number, setsWonB: number, matchType: MatchType): 'A' | 'B' | null {
  const config = MATCH_CONFIG[matchType];
  if (setsWonA >= config.winsNeeded) return 'A';
  if (setsWonB >= config.winsNeeded) return 'B';
  return null;
}

// Get eligible servers for a team
export function getEligibleServers(teamState: TeamState): { eligible: Player[]; ineligible: { player: Player; remaining: number }[] } {
  const serveHistory = teamState.rotationState.serveHistory;
  const requiredOthers = MIN_ROTATION_GAP - 1;

  const eligible: Player[] = [];
  const ineligible: { player: Player; remaining: number }[] = [];

  for (const player of teamState.players) {
    const lastServedIndex = serveHistory.lastIndexOf(player.id);
    if (lastServedIndex === -1) {
      eligible.push(player);
    } else {
      const playersAfterLastStint = new Set(serveHistory.slice(lastServedIndex + 1));
      if (playersAfterLastStint.size >= requiredOthers) {
        eligible.push(player);
      } else {
        ineligible.push({
          player,
          remaining: requiredOthers - playersAfterLastStint.size,
        });
      }
    }
  }

  return { eligible, ineligible };
}

// Calculate derived first serving team from toss
export function getFirstServingTeam(
  tossWonById: string,
  tossChoice: 'serve' | 'side',
  teamAId: string,
  teamBId: string
): string {
  if (tossChoice === 'serve') {
    return tossWonById;
  } else {
    return tossWonById === teamAId ? teamBId : teamAId;
  }
}

// Validate entire match setup
export function validateMatchSetup(data: {
  matchNumber: number | null;
  date: string | null;
  matchType: MatchType | null;
  teamAId: string | null;
  teamBId: string | null;
  tossWonById: string | null;
  tossChoice: 'serve' | 'side' | null;
}): ValidationError[] {
  const errors: ValidationError[] = [];

  const e1 = validateMatchNumberRequired(data.matchNumber);
  if (e1) errors.push(e1);

  if (data.matchNumber) {
    const e2 = validateMatchNumberFormat(data.matchNumber);
    if (e2) errors.push(e2);
  }

  const e3 = validateDateRequired(data.date);
  if (e3) errors.push(e3);

  const e4 = validateTeamRequired(null, 'Team A');
  if (!data.teamAId && e4) errors.push(e4);

  const e5 = validateTeamRequired(null, 'Team B');
  if (!data.teamBId && e5) errors.push(e5);

  const e6 = validateMatchTypeRequired(data.matchType);
  if (e6) errors.push(e6);

  const e7 = validateTossWinnerRequired(data.tossWonById);
  if (e7) errors.push(e7);

  const e8 = validateTossChoiceRequired(data.tossChoice);
  if (e8) errors.push(e8);

  const e9 = validateTeamsDifferent(data.teamAId, data.teamBId);
  if (e9) errors.push(e9);

  const e10 = validateTossWinnerInMatch(data.tossWonById, data.teamAId, data.teamBId);
  if (e10) errors.push(e10);

  return errors;
}
